/**
 * SLA aging/overdue model (report-a-concern.md "SLA / aging indicators + reminders") — the ONE
 * frontend mirror of services/api/internal/shared/sla.go, used by the mock store (so dev-with-mocks
 * exercises the same queue fields the live API returns) and by the QueuePage overdue chip/filter.
 * Business days only (skip Saturday/Sunday; NO holiday calendar). Terminal statuses are never
 * overdue. Defaults: acknowledge target 2 business days, resolve target 10 business days.
 */
import type { RequestStatus } from "@td/shared";

export const DEFAULT_SLA_ACK_DAYS = 2;
export const DEFAULT_SLA_RESOLVE_DAYS = 10;

/** Terminal statuses never age/overdue (mirrors shared.IsTerminal). */
const TERMINAL_STATUSES: ReadonlySet<RequestStatus> = new Set([
  "Resolved",
  "Closed",
  "Duplicate",
  "Invalid",
]);

export function isTerminalStatus(status: RequestStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** Whole business days (Mon-Fri) elapsed from `from` to `to`, skipping Sat/Sun — no holiday
 *  calendar. Normalizes both to UTC calendar dates first, mirroring BusinessDaysBetween (Go). */
export function businessDaysBetween(from: Date, to: Date): number {
  const fromDay = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const toDay = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  if (toDay <= fromDay) return 0;
  let days = 0;
  for (let d = fromDay + 86_400_000; d <= toDay; d += 86_400_000) {
    const wd = new Date(d).getUTCDay();
    if (wd !== 0 && wd !== 6) days++;
  }
  return days;
}

export function businessDaysSince(createdAtISO: string, now: Date): number {
  const t = new Date(createdAtISO);
  if (Number.isNaN(t.getTime())) return 0;
  return businessDaysBetween(t, now);
}

export type SLAStage = "acknowledge" | "resolve" | "";

export interface SLATargets {
  ackDays: number;
  resolveDays: number;
}

export interface SLAStatus {
  ageBusinessDays: number;
  overdue: boolean;
  slaStage: SLAStage;
  targetDays: number;
}

/** Fills in unset (0/undefined) targets with the package defaults (mirrors SLATargets.Resolved). */
export function resolveSLATargets(target?: Partial<SLATargets>): SLATargets {
  return {
    ackDays: target?.ackDays || DEFAULT_SLA_ACK_DAYS,
    resolveDays: target?.resolveDays || DEFAULT_SLA_RESOLVE_DAYS,
  };
}

/**
 * Resolve the effective SLA target for a request in `categoryId`: an override on the category
 * wins per-field; anything not overridden inherits the tenant base (itself defaulted). Mirrors
 * shared.EffectiveSLA.
 */
export function effectiveSLA(
  base: Partial<SLATargets> | undefined,
  categoryOverride: { ackDays?: number | null; resolveDays?: number | null } | undefined,
): SLATargets {
  const resolved = resolveSLATargets(base);
  if (categoryOverride?.ackDays != null) resolved.ackDays = categoryOverride.ackDays;
  if (categoryOverride?.resolveDays != null) resolved.resolveDays = categoryOverride.resolveDays;
  return resolved;
}

/**
 * Evaluate the aging/overdue status for one request as of `now` — mirrors shared.EvaluateSLA
 * exactly: terminal statuses are never overdue; a still-New request is overdue past the ack
 * target; any other non-terminal request is overdue past the resolve target. Only ONE stage can
 * breach at a time (New only checks ack; everything else only checks resolve).
 */
export function evaluateSLA(
  status: RequestStatus,
  createdAtISO: string,
  target: SLATargets,
  now: Date,
): SLAStatus {
  const ageBusinessDays = businessDaysSince(createdAtISO, now);
  if (isTerminalStatus(status)) {
    return { ageBusinessDays, overdue: false, slaStage: "", targetDays: 0 };
  }
  if (status === "New") {
    const overdue = ageBusinessDays > target.ackDays;
    return {
      ageBusinessDays,
      overdue,
      slaStage: overdue ? "acknowledge" : "",
      targetDays: target.ackDays,
    };
  }
  const overdue = ageBusinessDays > target.resolveDays;
  return {
    ageBusinessDays,
    overdue,
    slaStage: overdue ? "resolve" : "",
    targetDays: target.resolveDays,
  };
}

/** The aria-label / helper text naming the breach, derived from slaStage/targetDays (WCAG — never
 *  color-only). Returns null when not overdue. */
export function overdueAriaLabel(stage: SLAStage, targetDays: number): string | null {
  if (stage === "acknowledge") {
    return `Overdue: not acknowledged within ${targetDays} business day${targetDays === 1 ? "" : "s"}`;
  }
  if (stage === "resolve") {
    return `Overdue: not resolved within ${targetDays} business day${targetDays === 1 ? "" : "s"}`;
  }
  return null;
}

/**
 * Work-item due-date clock (work-item-due-dates.md) — the StaffOnly-category REPLACEMENT for
 * evaluateSLA above, mirroring services/api/internal/shared.EvaluateWorkItemDue exactly. A
 * StaffOnly `Kind=concern` category (e.g. "Flag Program", "Work Orders / Maintenance") is an
 * internal task-tracker row; the citizen ack/resolve business-day SLA is meaningless for it, so
 * overdue is driven ENTIRELY by the staff-set `dueDate` instead:
 *
 *  - Terminal statuses use the SAME narrow set the Go engine's `IsTerminal` uses for this clock
 *    (Closed/Duplicate/Invalid) — deliberately NOT `isTerminalStatus` above (which also treats
 *    Resolved as terminal for the citizen SLA's own purposes). `Resolved` is reopenable
 *    (Resolved->InProgress is a legal transition) so a resolved-but-still-past-due work item stays
 *    flagged, matching the backend's documented pragmatic-call deviation exactly.
 *  - An empty/absent dueDate is NEVER overdue — "no due date" means the item isn't tracked against
 *    a clock at all (never flagged just for aging in New, unlike the citizen SLA's ack-stage check).
 *  - A non-empty dueDate is a calendar date "YYYY-MM-DD" (today() > dueDate, NOT business days).
 *    An unparsable dueDate is treated as not overdue — never lock out on bad data.
 *
 * Returns the same SLAStatus shape evaluateSLA returns, so a queue row's shape stays one type
 * regardless of which clock computed it: ageBusinessDays and targetDays are always 0 (this clock
 * isn't business-day-based and has no fixed target); slaStage is "resolve" when overdue (single
 * stage — a work item has no ack/resolve distinction) else "".
 */
const WORK_ITEM_TERMINAL_STATUSES: ReadonlySet<RequestStatus> = new Set([
  "Closed",
  "Duplicate",
  "Invalid",
]);

export function evaluateWorkItemDue(
  status: RequestStatus,
  dueDate: string | undefined,
  now: Date,
): SLAStatus {
  if (!dueDate || WORK_ITEM_TERMINAL_STATUSES.has(status)) {
    return { ageBusinessDays: 0, overdue: false, slaStage: "", targetDays: 0 };
  }
  const d = new Date(dueDate + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) {
    return { ageBusinessDays: 0, overdue: false, slaStage: "", targetDays: 0 };
  }
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const due = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  if (today <= due) {
    return { ageBusinessDays: 0, overdue: false, slaStage: "", targetDays: 0 };
  }
  return { ageBusinessDays: 0, overdue: true, slaStage: "resolve", targetDays: 0 };
}

/** The aria-label / helper text for a due-date-driven overdue work item — a dedicated phrasing
 *  (never "within N business days", which doesn't apply to a calendar due date). Returns null when
 *  not overdue. */
export function overdueDueDateAriaLabel(dueDate: string): string {
  return `Overdue: past due date ${dueDate}`;
}
